import { faGear, faListCheck, faTableColumns } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissions } from '@front/hook/usePermissions';
import { L1Icon } from '@rt/global-navi/react';
import { createRootRoute, Outlet, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { ProductWrapper, useProject } from 'auth-lib-front';
import { type PropsWithChildren, useEffect } from 'react';
import { useGlobalNaviV2OnMenuNavigate } from './useGlobalNaviV2OnMenuNavigate';

// pid クエリパラメータの定義
type GlobalSearchParams = {
  pid?: string;
};

const ProjectIdSearchParamEnsurer = ({ children }: PropsWithChildren) => {
  const { project } = useProject();
  const navigate = useNavigate();
  const search = useSearch({ from: '__root__' });

  useEffect(() => {
    if (search.pid) return;
    navigate({
      search: (prev) => ({ ...prev, pid: project.id }) as never,
      replace: true,
    });
  }, [project.id, search.pid, navigate]);

  return children;
};

/**
 * @feature 顧客データベースのナビゲーション
 * @capabilities メニューからの機能切り替え, 権限に応じた設定への導線, 作業中プロジェクトの引き継ぎ
 * @behavior グローバルナビゲーションのメニューを選ぶとテーブル定義とタスク一覧を行き来でき、プロジェクト管理者と社内メンバーは設定にも入れる。画面を移動しても作業中のプロジェクトは引き継がれ、その URL を共有・再読み込みしても同じプロジェクトの続きから作業できる
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
const App = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '__root__' });
  const currentPath = useRouterState({ select: (state) => state.location.pathname });

  // ProductWrapper 配下の provider 経由で権限を見るため、メニュー構築は useMenuGroups 内で行う。
  // 「設定」配下は管理操作（プロジェクト管理者）と管理者設定（社内メンバー）向けなので、
  // どちらでもない一般メンバーには global navi に表示しない。
  const useMenuGroups = () => {
    const { isProjectAdmin, isEmployee } = usePermissions();

    const links = [
      {
        name: 'テーブル定義',
        url: '/table-definitions',
        icon: <FontAwesomeIcon icon={faTableColumns} className="size-4" aria-hidden />,
      },
      {
        name: 'タスク一覧',
        url: '/tasks',
        icon: <FontAwesomeIcon icon={faListCheck} className="size-4" aria-hidden />,
      },
      ...(isProjectAdmin || isEmployee
        ? [
            {
              name: '設定',
              url: '/settings',
              icon: <FontAwesomeIcon icon={faGear} className="size-4" aria-hidden />,
            },
          ]
        : []),
    ];

    return [
      {
        popOver: false as const,
        links,
      },
    ];
  };

  return (
    <ProductWrapper
      productKey="troppus"
      initialProjectId={search.pid}
      navigation={{
        title: {
          title: '顧客データベース',
          icon: <L1Icon icon="qantSupportDataHub" className="size-[18px]" />,
        },
        globalNaviV2: {
          useOnMenuNavigate: useGlobalNaviV2OnMenuNavigate,
        },
        menu: {
          useMenuGroups,
          isCurrentPath: (path) => currentPath === path || currentPath.startsWith(`${path}/`),
          navigate: (path, event) => {
            event?.preventDefault();
            return navigate({ to: path as never });
          },
        },
      }}
    >
      <ProjectIdSearchParamEnsurer>
        <Outlet />
      </ProjectIdSearchParamEnsurer>
    </ProductWrapper>
  );
};

export const Route = createRootRoute({
  validateSearch: (search): GlobalSearchParams => {
    const result: GlobalSearchParams = {};
    const searchProjectId = search.pid;
    if (typeof searchProjectId === 'string') {
      result.pid = searchProjectId;
    }
    return result;
  },
  component: App,
  search: {
    middlewares: [
      (props) => {
        const { search: current, next: n } = props;
        const next = n(current);

        // pidが未指定の場合は現在の値を引き継ぐ
        if (next.pid === undefined) {
          next.pid = current.pid;
        }

        return next;
      },
    ],
  },
});
